import { IApplicationVersion, ISubmittedApplication, SubmittedApplicationApiClient } from 'api/SubmittedApplicationApiClient'
import { IApplicationStatus } from 'pages/application/model/IApplicationStatus'
import { routes } from 'routing'
import { useApiClient, useSession, useTranslation } from '@hooks'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styles from './SubmittedApplicationPage.module.css'
import { ApplicationType, DPButton, SignatureType } from 'shared'
import { isCorporateApplicant } from 'shared/ApplicationHelper'
import { useAuthorization } from '@authentication'
import { isFeatureEnabled } from '@config'

interface IProps {
    referenceId: string,
    application: ISubmittedApplication,
    applicationStatus: IApplicationStatus,
    generateContract: () => void,
    applicationVersions: IApplicationVersion[] | undefined
}

export function SubmittedApplicationButtons(props: IProps) {
    const $t = useTranslation('SubmittedApplicationPage')
    const session = useSession()
    const navigate = useNavigate()
    const authorization = useAuthorization();
    const submittedApplicationApi = useApiClient(x => new SubmittedApplicationApiClient(x))
    const [contractUploadLink, setContractUploadLink] = useState<string>();
    const [downloadingXml, setDownloadingXml] = useState(false);

    const {
        referenceId,
        application,
        applicationStatus,
        generateContract

    } = props
    const isCorporate = application.type === ApplicationType.Loan && isCorporateApplicant(application.applicantType)
    const isPrivate = application.type === ApplicationType.Loan && !isCorporateApplicant(application.applicantType)
    const isLeasing = application.type === ApplicationType.Leasing
    const isPersonnel = application.type === ApplicationType.Personnel
    const isPaper = application.signatureTypeId === SignatureType.Paper
    // Corporate cases signed via SignForward (i.e. not paper) should use the
    // same "after contract generation" upload page as Private Leasing / Loans,
    // which has a single upload field plus product-specific documentation dropdowns.
    const isCorporateSignForward = isCorporate && !isPaper

    const queueContractGeneration = applicationStatus.shouldQueueContractGeneration;

    const dealerId = session.currentDealer!.id;

    const hasCoBuyer = application.coBuyerFullName.trim().length > 0;

    const objectDeliveredButtonVisible = applicationStatus.canDeliverObject

    const uploadDocumentsForMainApplicantButtonVisible =
        isCorporate
            ? isCorporateSignForward || !applicationStatus.hasContractEverBeenGenerated
            :
        (isPrivate || isPersonnel)
            ? !applicationStatus.hasContractEverBeenGenerated
            : !applicationStatus.canGenerateContract

    const uploadDocumentsForCoBuyerButtonVisible =
        isCorporate
            ? hasCoBuyer && (isCorporateSignForward || !applicationStatus.hasContractEverBeenGenerated)
            :
        (isPrivate || isPersonnel)
            ? !applicationStatus.hasContractEverBeenGenerated && hasCoBuyer
            : !applicationStatus.canGenerateContract && hasCoBuyer

    const contractUploadButtonVisible =
        isCorporate
            ? isPaper && applicationStatus.hasContractEverBeenGenerated
            :
        (isPrivate || isPersonnel)
            ? applicationStatus.hasContractEverBeenGenerated
                : applicationStatus.canGenerateContract

    // We also need the contract upload link for the corporate SignForward
    // "Upload documentation" button (see isCorporateSignForward above),
    // both before and after contract generation.
    const contractUploadLinkNeeded = contractUploadButtonVisible || isCorporateSignForward;         

    const generateContractButtonVisible =
        queueContractGeneration
            ? applicationStatus.canQueueContractGeneration
            : applicationStatus.canGenerateContract;

    const getSubmittedApplicationXmlVisible = authorization.hasAccessToSeeXmlSentToPreViewInSubmittedCases;

    const editApplicationButtonVisible = applicationStatus.canEditApplication 
        && isFeatureEnabled('submitted-application-edit') 
        && !isLeasing 
        && props.applicationVersions 
        && props.applicationVersions.length > 0

    const getSubmittedApplicationXml = async () => {
        setDownloadingXml(true);
        await submittedApplicationApi.getSubmittedApplicationXml(session.currentDealer!.id, referenceId!)
        setDownloadingXml(false);
    };

    const getLink = async (customer: "mainApplicant" | "cobuyer") => {
        const response = await submittedApplicationApi.getDocumentUploadLink(session.currentDealer!.id, referenceId)
        if (customer == 'mainApplicant')
            window.open(response?.mainBuyerLink, "_blank");
        else
            window.open(response?.coBuyerLink, "_blank");
    };

    useEffect(() => {
        if (!contractUploadLinkNeeded) {
            setContractUploadLink(undefined)
            return
        }

        submittedApplicationApi.generateContractUploadLink(dealerId, referenceId)
            .then(x => {
                if (x.success) {
                    setContractUploadLink(x.data?.url)
                }
                else {
                    setContractUploadLink(undefined)
                }
            })
    }, [contractUploadLinkNeeded])

    return (
        <div className={styles['buttons-container']}>
            <div>
                <DPButton size="mini" onClick={() => navigate(-1)} >{$t('Back')}</DPButton>
            </div>
            <div>
                {editApplicationButtonVisible && <DPButton
                    size="mini"
                    onClick={() => navigate(routes.applicationSubmittedApplicationEdit(referenceId))}
                >
                    {$t('Edit')}
                </DPButton>
                }
                {objectDeliveredButtonVisible &&
                    <Link
                        to={routes.objectDeliveredPage(referenceId)}
                        className="ui mini button"
                    >
                        {$t('Object delivered')}
                    </Link>
                }
                {uploadDocumentsForMainApplicantButtonVisible && (
                    isCorporateSignForward
                        // For corporate SignForward we link to the same upload page
                        // used by Private Leasing / Loans after contract generation.
                        ? <a
                            href={contractUploadLink}
                            className={`ui mini button ${!contractUploadLink ? 'disabled' : ''}`}
                            target='_blank'
                        >
                            {$t('Upload documentation')}
                        </a>
                        : <DPButton
                            size="mini"
                            onClick={() => getLink('mainApplicant')}
                        >
                            {isCorporate
                                ? $t('Upload documentation')
                                : $t('Upload documents for main applicant')}
                        </DPButton>

                    // <a
                    //     href={docLink.mainBuyerLink}
                    //     className={`ui mini button ${!docLink.mainBuyerLink ? 'disabled' : ''}`}
                    //     target='_blank'
                    // >
                    //     {$t('Upload documents for main applicant')}
                    // </a>
                )}
                {getSubmittedApplicationXmlVisible &&
                    <DPButton
                        size="mini"
                        onClick={getSubmittedApplicationXml}
                        disabled={downloadingXml}
                    >
                        {
                            $t('SeeXml')
                        }
                    </DPButton>
                }
                {uploadDocumentsForCoBuyerButtonVisible && !isCorporateSignForward &&

                    <DPButton
                        size="mini"
                        onClick={() => getLink('cobuyer')}
                    >
                        {isCorporate
                            ? $t('Upload documentation')
                            : $t('Upload documents for co applicant')}
                    </DPButton>

                    // <a
                    //     href={docLink.coBuyerLink}
                    //     className={`ui mini button ${!docLink.coBuyerLink ? 'disabled' : ''}`}
                    //     target='_blank'
                    // >
                    //     {$t('Upload documents for co applicant')}
                    // </a>
                }
                {contractUploadButtonVisible &&
                    <a
                        href={contractUploadLink}
                        className={`ui mini button ${!contractUploadLink ? 'disabled' : ''}`}
                        target='_blank'
                    >
                        {
                            isPrivate
                                ? $t('Upload dealer documents for main applicant')
                                : $t('Upload contract')
                        }
                    </a>
                }
                {generateContractButtonVisible &&
                    <DPButton
                        size="mini"
                        onClick={generateContract}
                    >
                        {
                            queueContractGeneration && applicationStatus.hasContractEverBeenGenerated
                                ? $t('Generate new contract')
                                : $t('Generate contract')
                        }
                    </DPButton>
                }
            </div>
        </div>
    )
}